】<template>
  <div class="game-container" @keydown="handleKeyPress" tabindex="0">
    <div class="game-header">
      <div class="header-content">
        <h1>{{ title }}</h1>
        <p class="instruction">{{ instruction }}</p>
      </div>
      
      <!-- 添加进度条到顶部 -->
      <div class="progress-row">
        <div class="level-info">进度: {{ Math.round(progress) }}%</div>
        <div class="progress">
          <div class="progress-bar" :style="{ width: progress + '%' }"></div>
        </div>
      </div>
    </div>

    <div class="game-content">
      <div class="stats-container">
        <div class="stat-box score">
          <div class="stat-icon">
            <span class="icon-circle">🎯</span>
          </div>
          <div class="stat-content">
            <div class="stat-value-container">
              <div class="stat-value">{{ score }}</div>
              <div class="stat-label">得分</div>
            </div>
          </div>
          <div class="score-change" v-if="scoreChange !== 0" :class="{ 'positive': scoreChange > 0, 'negative': scoreChange < 0 }">
            {{ scoreChange > 0 ? '+' + scoreChange : scoreChange }}
          </div>
        </div>
        <div class="stat-box accuracy">
          <div class="stat-icon">
            <span class="icon-circle">✨</span>
          </div>
          <div class="stat-content">
            <div class="stat-value-container">
              <div class="stat-value">{{ accuracy }}%</div>
              <div class="accuracy-bar">
                <div class="accuracy-fill" :style="{ width: accuracy + '%' }" :class="{ 'high': accuracy >= 90, 'medium': accuracy >= 70 && accuracy < 90, 'low': accuracy < 70 }"></div>
              </div>
            </div>
            <div class="stat-label">正确率</div>
          </div>
        </div>
      </div>

      <div class="typing-area" :class="{ correct: isCorrect, wrong: isWrong }">
        <div v-if="currentKeyword" class="keyword-info">
          <!-- 正向模式：显示词汇和描述 -->
          <template v-if="!isReverseMode">
            <div class="keyword-name">{{ currentKeyword.name }}</div>
            <div class="keyword-desc">{{ currentKeyword.desc }}</div>
          </template>
          <!-- 反向模式：显示描述作为提示 -->
          <template v-else>
            <div class="keyword-question">这个词汇是什么?</div>
            <div class="keyword-name reverse">{{ currentKeyword.desc }}</div>
            <div class="keyword-hint" v-if="showHint">
              <span class="hint-label">提示:</span> 
              <span class="hint-text">首字母 "{{ currentKeyword.name[0] }}"</span>
            </div>
          </template>
          <div class="audio-hint">按空格键听发音</div>
        </div>
        <!-- 正向模式显示字母提示 -->
        <div v-if="!isReverseMode" class="target-letters">
          <span 
            v-for="(letter, index) in targetLetters" 
            :key="index"
            :class="{
              'current': index === currentIndex,
              'completed': index < currentIndex
            }"
          >{{ letter }}</span>
        </div>
        <!-- 反向模式显示用户输入的内容 -->
        <div v-else class="user-input">
          <span class="input-prompt">请输入对应的词汇：</span>
          <div class="input-display">
            <span class="typed-text">{{ typedText }}</span>
            <span class="cursor-indicator"></span>
          </div>
        </div>
        <!-- 正向模式显示当前字母指示 -->
        <div v-if="!isReverseMode" class="letter-instruction">
          请输入 
          <span class="letter-highlight" :class="{ 'uppercase': /[A-Z]/.test(currentLetter), 'special-char': /[!@#$%^&*()_+{}|:<>?]/.test(currentLetter) }">
            {{ currentLetter }}
            <span v-if="/[A-Z]/.test(currentLetter)" class="uppercase-hint">大写</span>
            <span v-else-if="/[!@#$%^&*()_+{}|:<>?]/.test(currentLetter)" class="special-char-hint">按住Shift</span>
          </span>
        </div>
        <!-- 反向模式添加提示按钮 -->
        <div v-else class="hint-container">
          <button @click="toggleHint" class="hint-button">
            <span class="button-icon">💡</span>
            {{ showHint ? '隐藏提示' : '显示提示' }}
          </button>
        </div>
      </div>

      <!-- 添加模式切换按钮 -->
      <div class="mode-switch-container">
        <button 
          @click="toggleMode" 
          class="mode-switch-button"
          :class="{ 'active': isReverseMode }"
        >
          <span class="button-icon">🔄</span>
          {{ isReverseMode ? '含义→词汇' : '词汇→含义' }}
        </button>
        <div class="mode-description">
          {{ isReverseMode ? '看到含义，输入对应词汇' : '看到词汇，输入完整拼写' }}
        </div>
      </div>

      <div class="practice-keyboard">
        <div class="practice-row">
          <div 
            v-for="(key, index) in ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || (key === '1' && currentLetter === '!') || (key === '2' && currentLetter === '@') || (key === '3' && currentLetter === '#') || (key === '4' && currentLetter === '$') || (key === '5' && currentLetter === '%') || (key === '6' && currentLetter === '^') || (key === '7' && currentLetter === '&') || (key === '8' && currentLetter === '*') || (key === '9' && currentLetter === '(') || (key === '0' && currentLetter === ')') || (key === '-' && currentLetter === '_') || (key === '=' && currentLetter === '+') || (key === '`' && currentLetter === '~')) }"
          >
            <span class="key-top">{{ index === 0 ? '~' : index === 1 ? '!' : index === 2 ? '@' : index === 3 ? '#' : index === 4 ? '$' : index === 5 ? '%' : index === 6 ? '^' : index === 7 ? '&' : index === 8 ? '*' : index === 9 ? '(' : index === 10 ? ')' : index === 11 ? '_' : '+' }}</span>
            <span class="key-bottom">{{ key }}</span>
          </div>
          <div class="practice-key backspace-key">←</div>
        </div>
        <div class="practice-row">
          <div class="practice-key tab-key">Tab</div>
          <div 
            v-for="key in ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', '[', ']']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || currentLetter === key.toLowerCase() || (key === '[' && currentLetter === '{') || (key === ']' && currentLetter === '}')) }"
          >
            <span v-if="key === '[' || key === ']'">
              <span class="key-top">{{ key === '[' ? '{' : '}' }}</span>
              <span class="key-bottom">{{ key }}</span>
            </span>
            <span v-else class="key-single">{{ key }}</span>
          </div>
          <div class="practice-key backslash-key">
            <span class="key-top">|</span>
            <span class="key-bottom">\</span>
          </div>
        </div>
        <div class="practice-row">
          <div class="practice-key caps-key">Caps</div>
          <div 
            v-for="key in ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', ';', '\'']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || currentLetter === key.toLowerCase() || (key === ';' && currentLetter === ':') || (key === '\'' && currentLetter === '\'')) }"
          >
            <span v-if="key === ';' || key === '\''">
              <span class="key-top">{{ key === ';' ? ':' : '&quot;' }}</span>
              <span class="key-bottom">{{ key }}</span>
            </span>
            <span v-else class="key-single">{{ key }}</span>
          </div>
          <div class="practice-key enter-key">Enter</div>
        </div>
        <div class="practice-row">
          <div class="practice-key shift-key" :class="{ 'active-shift': !isReverseMode && isShiftRequired(currentLetter) }">Shift</div>
          <div 
            v-for="key in ['Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '/']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': !isReverseMode && (currentLetter === key || currentLetter === key.toLowerCase() || (key === ',' && currentLetter === '<') || (key === '.' && currentLetter === '>') || (key === '/' && currentLetter === '?')) }"
          >
            <span v-if="key === ',' || key === '.' || key === '/'">
              <span class="key-top">{{ key === ',' ? '&lt;' : key === '.' ? '&gt;' : '?' }}</span>
              <span class="key-bottom">{{ key }}</span>
            </span>
            <span v-else class="key-single">{{ key }}</span>
          </div>
          <div class="practice-key shift-key-right" :class="{ 'active-shift': !isReverseMode && isShiftRequired(currentLetter) }">Shift</div>
        </div>
        <div class="practice-row">
          <div class="practice-key ctrl-key">Ctrl</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key space-key" :class="{ 'active': !isReverseMode && currentLetter === ' ' }">空格</div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key menu-key">Menu</div>
          <div class="practice-key ctrl-key">Ctrl</div>
        </div>
      </div>

     
      
      <!-- 添加退出按钮 -->
      <div class="control-buttons">
        <button @click="exitPractice" class="exit-button">
          <span class="button-icon">🚪</span>
          退出练习
        </button>
      </div>
    </div>
    <!-- 添加打字报告弹窗 -->
    <div class="report-modal" v-if="showComplete">
      <div class="report-content">
        <h2>{{ title }}练习报告</h2>
        <div class="report-stats">
          <div class="stat-item">
            <div class="stat-label">练习用时</div>
            <div class="stat-value">{{ formatTime(totalTime) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">最终得分</div>
            <div class="stat-value">{{ score }}分</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">正确率</div>
            <div class="stat-value">{{ accuracy }}%</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">打字速度</div>
            <div class="stat-value">{{ typingSpeed }}个/分钟</div>
          </div>
        </div>
        
        <div class="report-message">
          <div class="result-container">
            <img :src="resultImage" :alt="resultMessage">
            <p>{{ resultMessage }}</p>
          </div>
        </div>
        
        <div class="report-buttons">
          <button @click="restartLevel">关闭</button>
        </div>
      </div>
    </div>

    
  </div>
</template>

<script>
export default {
  name: 'TypingPlanetKeywords',
  data() {
    return {
      gameStarted: true,
      currentLetter: '',
      targetLetters: '',
      currentIndex: 0,
      score: 0,
      totalAttempts: 0,
      correctAttempts: 0,
      accuracy: 100,
      isCorrect: false,
      isWrong: false,
      progress: 0,
      showComplete: false,
      combinations: 15,
      scoreChange: 0,
      scoreChangeTimeout: null,
      title: '',
      instruction: '',
      levelId: '',
      nextLevel: '',
      startTime: null,
      totalTime: 0,
      typingSpeed: 0,
      resultMessage: '',
      resultImage: '',
      targetGroup: 'group1', // 默认分组
      currentKeyword: null, // 当前词汇对象
      resultImages: {
        excellent: 'https://wugecdn.steam.fun/typefun/images/excellent.png',
        good: 'https://wugecdn.steam.fun/typefun/images/good.png',
        keepTrying: 'https://wugecdn.steam.fun/typefun/images/keep-trying.png'
      },
      keywords: [
        {
          name:'if',
          desc:'条件判断',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/if.mp3',
          group:'group1'
        },
        {
          name:'else',
          desc:'条件分支',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/else.mp3',
          group:'group1'
        },
        {
          name:'for',
          desc:'遍历循环',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/for.mp3',
          group:'group1'
        },
        {
          name:'while',
          desc:'条件循环',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/while.mp3',
          group:'group1'
        },
        {
          name:'import',
          desc:'导入模块',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/import.mp3',
          group:'group1'
        },
        {
          name:'and',
          desc:'逻辑与',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/and.mp3',
          group:'group2'
        },
        {
          name:'or',
          desc:'逻辑或',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/or.mp3',
          group:'group2'
        },
        {
          name:'not',
          desc:'逻辑非',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/not.mp3',
          group:'group2'
        },
        {
          name:'True',  
          desc:'真',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/true.mp3',
          group:'group2'
        },
        {
          name:'False',
          desc:'假',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/false.mp3',
          group:'group2'
        },
        {
          name:'in',
          desc:'成员检查',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/in.mp3',
          group:'group2'
        },
        {
          name:'is',
          desc:'对象身份比较',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/is.mp3',
          group:'group2'
        },
        {
          name:'def',
          desc:'定义函数',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/def.mp3',
          group:'group3'
        },
        {
          name:'return',
          desc:'返回值',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/return.mp3',
          group:'group3'
        },
        {
          name:'global',
          desc:'全局变量',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/global.mp3',
          group:'group3'
        },
        {
          name:'lambda',
          desc:'匿名函数',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/lambda.mp3',
          group:'group3'
        },
        {
          name:'pass',
          desc:'占位符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/pass.mp3',
          group:'group3'
        },
        {
          name:'try',
          desc:'异常处理',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/try.mp3',
          group:'group4'
        },
        {
          name:'except',
          desc:'异常处理',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/except.mp3',
          group:'group4'
        },
        {
          name:'finally',
          desc:'异常处理',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/finally.mp3',
          group:'group4'
        },
        { 
          name:'raise',
          desc:'抛出异常',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/raise.mp3',
          group:'group4'
        },
        {
          name:'with',
          desc:'上下文管理',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/with.mp3',
          group:'group4'
        },
        {
          name:'class',
          desc:'类',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/class.mp3',
          group:'group5'
        },
        {
          name:'self',
          desc:'类实例',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/self.mp3',
          group:'group5'
        }, 
        {
          name:'del',
          desc:'删除对象',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/del.mp3',
          group:'group6'
        },
        {
          name:'break',
          desc:'跳出循环',
          audioUrl:'https://static01.typ  .fun/course/vocabulary-audio/break.mp3',
          group:'group6'
        },
        {
          name:'continue',
          desc:'继续循环',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/continue.mp3',
          group:'group6'
        },
        {
          name:'elif',
          desc:'条件分支',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/elif.mp3',
          group:'group6'
        },
        {
          name:'yield',
          desc:'生成器',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/yield.mp3',
          group:'group7'
        },
        {
          name:'nonlocal',
          desc:'非局部变量',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/nonlocal.mp3',
          group:'group7'
        },
        {
          name:'assert',
          desc:'断言',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/assert.mp3',
          group:'group7'
        },
        {
          name:'as',
          desc:'别名',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/as.mp3',
          group:'group7'
        },
        {
          name:'from',
          desc:'从...导入',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/from.mp3',
          group:'group7'
        },
        {
          name:'async',
          desc:'异步',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/async.mp3',
          group:'group7'
        },
        {
          name:'await',
          desc:'等待',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/await.mp3',
          group:'group7'
        },
        {
          name:'None',
          desc:'空',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/none.mp3',
          group:'group7'
        },
        {
          name:'except',
          desc:'异常处理',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/except.mp3',
          group:'group7'
        },

        {
          name:'+',
          desc:'加号',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/plus.mp3',
          group:'group8'
        },
        {
          name:'-',
          desc:'减号',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/minus.mp3',
          group:'group8'
        },
        {
          name:'*',
          desc:'乘号',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/multiply.mp3',
          group:'group8' 
        },
        {
          name:'/',
          desc:'除号',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/divide.mp3',
          group:'group8'
        },
        {
          name:'//',
          desc:'整除',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group8'
        },
        {
          name:'%',
          desc:'取余',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group8'
        },
        {
          name:'**',
          desc:'幂运算',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group8'
        },
        {
          name:'==',
          desc:'等于（值比较）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group9'
        },
        {
          name:'!=',
          desc:'不等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group9'
        },
        {
          name:'>',
          desc:'大于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group9'
        },
        {
          name:'<',
          desc:'小于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group9'
        },
        {
          name:'>=',
          desc:'大于等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group9'
        },
        {
          name:'<=',
          desc:'小于等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group9'
        },
        {
          name:'=',
          desc:'赋值（如 x = 5）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'+=',
          desc:'加法赋值（如 x += 3 等价于 x = x + 3）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'+=',
          desc:'加法赋值（如 x += 3 等价于 x = x + 3）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'-=',
          desc:'减法赋值（如 x -= y 等同于 x = x - y）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'*=',
          desc:'乘法赋值（如 x *= 2 等同于 x = x * 2）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'/=',
          desc:'浮点除法赋值（如 x /= 2 等同于 x = x / 2）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'//=',
          desc:'整数除法赋值（如 x //= y 等同于 x = x // y）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'%=',
          desc:'取余赋值（如 x %= 3 等同于 x = x % 3）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'**=',
          desc:'幂运算赋值（如 x **= 2 等同于 x = x ** 2）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group10'
        },
        {
          name:'&',
          desc:'按位与',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group11'
        },
        {
          name:'|',
          desc:'按位或',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group11'
        },
        {
          name:'^',
          desc:'按位异或',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group11'
        },
        {
          name:'~',
          desc:'按位取反',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group11'
        },
        {
          name:'<<',
          desc:'左移',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group11'
        },
        {
          name:'>>',
          desc:'右移',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group11'
        },
        {
          name:':',
          desc:'代码块开始（如 if、for、函数定义后）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'()',
          desc:'函数调用、元组、优先级调整',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'[]',
          desc:'列表、索引',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'{}',
          desc:'字典、集合',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'@',
          desc:'装饰器',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'#',
          desc:'注释',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'.',
          desc:'属性访问（如 os.path）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:',',
          desc:'分隔符（如列表、元组、字典）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:';',
          desc:'语句结束（如 for、if、while）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'->',
          desc:'返回类型（如 def func() -> int）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/modulo.mp3',
          group:'group12'
        },
        {
          name:'turtle',
          desc:'海龟',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/turtle.mp3',
          group:'group13'
        },
        {
          name:'forward',
          desc:'前进',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/forward.mp3',
          group:'group13'
        },
        {
          name:'backward',
          desc:'后退',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/backward.mp3',
          group:'group13'
        },
        {
          name:'left',
          desc:'左转',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/left.mp3',
          group:'group13'
        },
        {
          name:'right',
          desc:'右转',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/right.mp3',
          group:'group13'
        },
        {
          name:'goto',
          desc:'移动到',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/goto.mp3',
          group:'group13'
        },
        {
          name:'penup',
          desc:'抬笔',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/penup.mp3',
          group:'group13'
        },
        {
          name:'pendown',
          desc:'落笔',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/pendown.mp3',
          group:'group13'
        },
        {
          name:'pensize',
          desc:'笔的大小',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/pensize.mp3',
          group:'group13'
        },
        {
          name:'pencolor',
          desc:'笔的颜色',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/pencolor.mp3',
          group:'group13'
        },
        {
          name:'begin_fill',
          desc:'开始填充',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/begin_fill.mp3',
          group:'group13'
        },
        {
          name:'fillcolor',
          desc:'填充颜色',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/fillcolor.mp3',
          group:'group13'
        },
        {
          name:'end_fill',
          desc:'结束填充',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/end_fill.mp3',
          group:'group13'
        },
        {
          name:'circle',
          desc:'画圆',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/circle.mp3',
          group:'group13'
        },
        {
          name:'dot',
          desc:'画点',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/dot.mp3',
          group:'group13'
        },
        {
          name:'write',
          desc:'写字',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/write.mp3',
          group:'group13'
        },
        {
          name:'setup',
          desc:'设置海龟',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/setup.mp3',
          group:'group14'
        },
        {
          name:'screensize',
          desc:'设置屏幕大小',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/screensize.mp3',
          group:'group14'
        },
        {
          name:'bgcolor',
          desc:'设置背景颜色',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/bgcolor.mp3',
          group:'group14'
        },
        {
          name:'showturtle',
          desc:'显示海龟',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/showturtle.mp3',
          group:'group14'
        },
        {
          name:'hideturtle',
          desc:'隐藏海龟',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/hideturtle.mp3',
          group:'group14'
        },
        {
          name:'shape',
          desc:'形状',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/shape.mp3',
          group:'group14'
        },
        {
          name:'speed',
          desc:'速度',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/speed.mp3',
          group:'group14'
        },
        {
          name:'setposition',
          desc:'设置位置',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/setposition.mp3',
          group:'group14'
        },
        {
          name:'setheading',
          desc:'设置方向',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/setheading.mp3',
          group:'group14'
        },
        {
          name:'title',
          desc:'标题',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/title.mp3',
          group:'group14'
        },
        {
          name:'done',
          desc:'完成',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/python/done.mp3',
          group:'group14'
        },
        
        {
          name:'#include',
          desc:'包含头文件',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/include.mp3',
          group:'group31'
        },
        {
          name:'using',
          desc:'使用命名空间',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/using.mp3',
          group:'group31'
        },
        {
          name:'namespace',
          desc:'命名空间',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/namespace.mp3',
          group:'group31'
        },
        {
          name:'int',
          desc:'整数类型',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/int.mp3',
          group:'group31'
        },
        {
          name:'main',
          desc:'主函数',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/main.mp3',
          group:'group31'
        },
        {
          name:'return',
          desc:'返回',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/return.mp3',
          group:'group31'
        },
        {
          name:'int',
          desc:'整数类型',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/int.mp3',
          group:'group32'
        },
        {
          name:'double',
          desc:'双精度浮点数类型',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/double.mp3',
          group:'group32'
        },
        {
          name:'char',
          desc:'字符类型',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/char.mp3',
          group:'group32'
        },
        {
          name:'bool',
          desc:'布尔类型',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/bool.mp3',
          group:'group32'
        },
        {
          name:'long',
          desc:'长整型，用于存储较大的整数;long long 超长整型，用于存储更大的整数',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/long.mp3',
          group:'group32'
        },
        {
          name:'unsigned',
          desc:'无符号整数类型',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/unsigned.mp3',
          group:'group32'
        },
        {
          name:'const',
          desc:'常量',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/const.mp3',
          group:'group32'
        },
        {
          name:'cin',
          desc:'输入',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/cin.mp3',
          group:'group33'
        },
        {
          name:'cout',
          desc:'输出',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/cout.mp3',
          group:'group33'
        },
        {
          name:'scanf',
          desc:'输入',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/scanf.mp3',
          group:'group33'
        },
        {
          name:'printf',
          desc:'输出',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/printf.mp3',
          group:'group33'
        },
        {
          name:'puts',
          desc:'输出',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/puts.mp3',
          group:'group33'
        },
        {
          name:'if',
          desc:'条件语句',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/if.mp3',
          group:'group34'
        },
        {
          name:'else',
          desc:'条件语句',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/else.mp3',
          group:'group34'
        },
        {
          name:'for',
          desc:'循环语句',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/for.mp3',
          group:'group34'
        },
        {
          name:'while',
          desc:'循环语句',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/while.mp3',
          group:'group34'
        },
        {
          name:'switch',
          desc:'条件语句',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/switch.mp3',
          group:'group34'
        },
        {
          name:'case',
          desc:'条件语句',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/case.mp3',
          group:'group34'
        },
        {
          name:'break',
          desc:'跳出循环',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/break.mp3',
          group:'group34'
        },
        {
          name:'continue',
          desc:'跳过当前循环剩余代码，进入下一次循环',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/continue.mp3',
          group:'group34'
        },
        {
          name:'void',
          desc:'空类型',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/void.mp3',
          group:'group35'
        },
        {
          name:'return',
          desc:'返回',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/return.mp3',
          group:'group35'
        },
        {
          name:'new',
          desc:'动态内存分配',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/new.mp3',
          group:'group35'
        },
        {
          name:'delete',
          desc:'释放动态内存',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/delete.mp3',
          group:'group35'
        },
        {
          name:'sizeof',
          desc:'获取变量或类型所占内存大小',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/sizeof.mp3',
          group:'group35'
        },
        {
          name:'struct',
          desc:'定义结构体',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/struct.mp3',
          group:'group36'
        },
        {
          name:'class',
          desc:'定义类',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/class.mp3',
          group:'group36'
        },
        {
          name:'public',
          desc:'公共成员',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/public.mp3',
          group:'group36'
        },
        {
          name:'private',
          desc:'私有成员',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/private.mp3',
          group:'group36'
        },
        {
          name:'protected',
          desc:'受保护成员',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/protected.mp3',
          group:'group36'
        },
        {
          name:'vector',
          desc:'向量',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/vector.mp3',
          group:'group37'
        },
        {
          name:'string',
          desc:'字符串',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/string.mp3',
          group:'group37'
        },
        {
          name:'map',
          desc:'映射',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/map.mp3',
          group:'group37'
        },
        {
          name:'queue',
          desc:'队列',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/queue.mp3',
          group:'group37'
        },
        {
          name:'stack',
          desc:'栈',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/stack.mp3',
          group:'group37'
        },
        {
          name:'try',
          desc:'异常处理',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/try.mp3',
          group:'group38'
        },
        {
          name:'catch',
          desc:'异常处理',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/catch.mp3',
          group:'group38'
        },
        {
          name:'throw',
          desc:'抛出异常',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/throw.mp3',
          group:'group38'
        },
        {
          name:'+',
          desc:'加法运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/plus.mp3',
          group:'group40'
        },
        {
          name:'-',
          desc:'减法运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/minus.mp3',
          group:'group40'
        },
        {
          name:'*',
          desc:'乘法运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/multiply.mp3',
          group:'group40'
        },
        {
          name:'/',
          desc:'除法运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/divide.mp3',
          group:'group40'
        },
        {
          name:'%',
          desc:'取模运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/modulo.mp3',
          group:'group40'
        },
        {
          name:'++',
          desc:'自增运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/increment.mp3',
          group:'group40'
        },
        {
          name:'--',
          desc:'自减运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/decrement.mp3',
          group:'group40'
        },
        {
          name:'==',
          desc:'等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/equal.mp3',
          group:'group41'
        },
        {
          name:'!=',
          desc:'不等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/notequal.mp3',
          group:'group41'
        },
        {
          name:'<',
          desc:'小于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/lessthan.mp3',
          group:'group41'
        },
        {
          name:'>',
          desc:'大于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/greaterthan.mp3',
          group:'group41'
        },
        {
          name:'<=',
          desc:'小于等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/lessthanequal.mp3',
          group:'group41'
        },
        {
          name:'>=',
          desc:'大于等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/greaterthanequal.mp3',
          group:'group41'
        },
        {
          name:'&&',
          desc:'逻辑与',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/logicaland.mp3',
          group:'group42'
        },
        {
          name:'||',
          desc:'逻辑或',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/logicalor.mp3',
          group:'group42'
        },
        {
          name:'!',
          desc:'逻辑非',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/logicalnot.mp3',
          group:'group42'
        },
        {
          name:'=',
          desc:'赋值',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/equal.mp3',
          group:'group43'
        },
        {
          name:'+=',
          desc:'加等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/plusequal.mp3',
          group:'group43'
        },
        {
          name:'-=',
          desc:'减等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/minusequal.mp3',
          group:'group43'
        },
        {
          name:'*=',
          desc:'乘等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/multiplyequal.mp3',
          group:'group43'
        },
        {
          name:'/=',
          desc:'除等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/divideequal.mp3',
          group:'group43'
        },
        {
          name:'?:',
          desc:'三目运算符,表达式1 ? 表达式2 : 表达式3',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/sanmuyunsuanfu.mp3',
          group:'group43'
        },
        {
          name:'&',
          desc:'取地址',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/addressof.mp3',
          group:'group44'
        },
        {
          name:'|',
          desc:'按位或',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/bitwiseor.mp3',
          group:'group44'
        },
        {
          name:'^',
          desc:'按位异或',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/bitwisexor.mp3',
          group:'group44'
        },
        {
          name:'~',
          desc:'按位取反',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/bitwiseflip.mp3',
          group:'group44'
        },
        {
          name:'<<',
          desc:'左移',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/leftshift.mp3',
          group:'group44'
        },
        {
          name:'>>',
          desc:'右移',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/rightshift.mp3',
          group:'group44'
        },
        {
          name:'<<=',
          desc:'左移等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/leftshiftequal.mp3',
          group:'group44'
        },
        {
          name:'>>=',
          desc:'右移等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/rightshiftequal.mp3',
          group:'group44'
        },
        {
          name:'&=',
          desc:'按位与等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/bitwiseandequal.mp3',
          group:'group44'
        },
        {
          name:'|=',
          desc:'按位或等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/bitwiseorequal.mp3',
          group:'group44'
        },
        {
          name:'^=',
          desc:'按位异或等于',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/bitwisexorequal.mp3',
          group:'group44'
        },
        {
          name:'<<',
          desc:'输出数据到流（如 cout）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/scoperesolution.mp3',
          group:'group45'
        },
        {
          name:'>>',
          desc:'输入数据从流（如 cin）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/empty.mp3',
          group:'group45'
        },
        {
          name:'""',
          desc:'定义字符串字面量',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/stringliteral.mp3',
          group:'group45'
        },
        {
          name:'\\n',
          desc:'换行符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/newline.mp3',
          group:'group45'
        },
        {
          name:'%d',
          desc:'格式化整数（int）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/formatinteger.mp3',
          group:'group45'
        },
        {
          name:'%lld',
          desc:'格式化长整数（long long）',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/formatlonglong.mp3',
          group:'group45'
        },
        {
          name:'()',
          desc:'函数调用',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/functioncall.mp3',
          group:'group46'
        },
        {
          name:'{}',
          desc:'初始化列表',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/initializerlist.mp3',
          group:'group46'
        },
        {
          name:'[]',
          desc:'数组下标访问',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/arrayindex.mp3',
          group:'group46'
        },
        {
          name:',',
          desc:'逗号运算符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/comma.mp3',
          group:'group46'
        },
        {
          name:';',
          desc:'语句结束符',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/semicolon.mp3',
          group:'group46'
        },
        {
          name:'->',
          desc:'成员访问',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/memberaccess.mp3',
          group:'group46'
        },
        {
          name:'.',
          desc:'成员访问',
          audioUrl:'https://wugecdn.steam.fun/typefun/music/cplus/memberaccess.mp3',
          group:'group46'
        }
      ],
      isReverseMode: false,
      showHint: false,
      typedText: ''
    }
  },
  methods: {
    generateNewLetter() {
      // 从路由参数获取的分组中筛选词汇
      const groupKeywords = this.keywords.filter(keyword => keyword.group === this.targetGroup);
      
      // 如果没有找到对应分组的词汇，使用所有词汇
      const availableKeywords = groupKeywords.length > 0 ? groupKeywords : this.keywords;
      
      // 随机选择一个词汇
      const randomKeyword = availableKeywords[Math.floor(Math.random() * availableKeywords.length)];
      
      // 设置当前词汇对象
      this.currentKeyword = randomKeyword;
      
      // 根据模式不同，设置不同的目标和提示
      if (this.isReverseMode) {
        // 反向模式：目标是输入完整的词汇
        this.typedText = '';
        this.targetLetters = randomKeyword.name; // 这里实际上是期望用户输入的内容
      } else {
        // 正向模式：显示词汇，输入一个个字母
        this.targetLetters = randomKeyword.name;
        this.currentIndex = 0;
        this.currentLetter = this.targetLetters[0];
      }
      
      // 重置提示状态
      this.showHint = false;
    },

    playCurrentAudio() {
      if (this.currentKeyword && this.currentKeyword.audioUrl) {
        const audio = new Audio(this.currentKeyword.audioUrl);
        audio.play();
      }
    },

    handleKeyPress(event) {
      if (!this.gameStarted || this.showComplete) return
      
      // 添加调试信息
      console.log('按下的键:', event.key, '当前期望字母:', this.currentLetter, 'keyCode:', event.keyCode)
      
      // 定义需要忽略的修饰键列表
      const modifierKeys = [
        'CapsLock', 'Shift', 'Control', 'Alt', 'Meta', 'Tab', 'Escape', 
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Home', 'End', 'PageUp', 'PageDown', 'Insert', 'Delete',
        'NumLock', 'ScrollLock', 'Pause', 'PrintScreen',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
      ]
      
      // 播放音频功能（空格键）
      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault()
        if(event.code === 'Space' ){
          this.playCurrentAudio()
        }
        return
      }
      
      // 如果按下的是修饰键，直接返回，不计入尝试次数
      if (modifierKeys.includes(event.key)) {
        return
      }
      
      // 处理反向模式
      if (this.isReverseMode) {
        if (event.key === 'Backspace') {
          // 处理退格键
          this.typedText = this.typedText.slice(0, -1);
          return;
        } else if (event.key === 'Enter') {
          // 处理回车键，验证答案
          this.totalAttempts++;
          
          if (this.typedText.toLowerCase() === this.targetLetters.toLowerCase()) {
            this.handleReverseCorrectAnswer();
          } else {
            this.handleReverseWrongAnswer();
          }

          this.isReverseMode = false;//
          
          return;
        } else if (event.key.length === 1) {
          // 普通字符输入
          this.typedText += event.key;
        }
        
        return;
      }
      
      // 正向模式的逻辑
      this.totalAttempts++
      
      // 严格的大小写字母处理逻辑
      if (event.key === this.currentLetter) {
        this.handleCorrectKey()
      } else {
        this.handleWrongKey()
      }
      
      this.accuracy = Math.round((this.correctAttempts / this.totalAttempts) * 100)
    },

    handleCorrectKey() {
      this.isCorrect = true
      this.correctAttempts++
      
      this.showScoreChange(10)
      this.score += 10

      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/correct.mp3')
      audio.play()

      this.currentIndex++
      
      if (this.currentIndex >= this.targetLetters.length) {
        const progressIncrement = 100 / this.combinations
        this.progress += progressIncrement

        if (this.progress >= 100) {
          this.completeLevelAndSave()
        } else {
          setTimeout(() => {
            this.generateNewLetter()
          }, 300)
        }
      } else {
        this.currentLetter = this.targetLetters[this.currentIndex]
      }

      setTimeout(() => {
        this.isCorrect = false
      }, 300)
    },

    handleWrongKey() {
      this.isWrong = true
      
      this.showScoreChange(-5)
      this.score = Math.max(0, this.score - 5)

      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/fail.mp3')
      audio.play()

      setTimeout(() => {
        this.isWrong = false
      }, 300)
    },

    completeLevelAndSave() {
      const completedLevels = JSON.parse(localStorage.getItem('completedLevels') || '[]')
      if (!completedLevels.includes(this.levelId)) {
        completedLevels.push(this.levelId)
        localStorage.setItem('completedLevels', JSON.stringify(completedLevels))
      }
      
      // 计算练习时间和打字速度
      const endTime = new Date()
      this.totalTime = (endTime - this.startTime) / 1000
      this.typingSpeed = Math.round((this.correctAttempts / (this.totalTime / 60)))
      
      // 根据得分和正确率设置结果消息和图片
      if (this.score >= 300 && this.accuracy >= 90) {
        this.resultMessage = '太棒了！你掌握了这些词汇！'
        this.resultImage = this.resultImages.excellent
      } else if (this.score >= 200 && this.accuracy >= 80) {
        this.resultMessage = '不错的进步！继续练习这些词汇！'
        this.resultImage = this.resultImages.good
      } else {
        this.resultMessage = '坚持练习，词汇需要多次重复记忆！'
        this.resultImage = this.resultImages.keepTrying
      }
      
      this.showComplete = true
    },

    exitPractice() {
      this.$router.back()
    },

    restartLevel() {
      this.score = 0
      this.totalAttempts = 0
      this.correctAttempts = 0
      this.accuracy = 100
      this.progress = 0
      this.showComplete = false
      this.generateNewLetter()
      this.$el.focus()
      this.startTime = new Date()
      
      // 重置其他状态
      this.isCorrect = false
      this.isWrong = false
      this.scoreChange = 0
      this.typingSpeed = 0
      
      // 确保游戏状态为开始
      this.gameStarted = true
      
      // 清除任何可能存在的计时器
      if (this.scoreChangeTimeout) {
        clearTimeout(this.scoreChangeTimeout)
      }
    },

    showScoreChange(change) {
      this.scoreChange = change
      
      if (this.scoreChangeTimeout) {
        clearTimeout(this.scoreChangeTimeout)
      }
      
      this.scoreChangeTimeout = setTimeout(() => {
        this.scoreChange = 0
      }, 1000)
    },

    loadRouteParams() {
      // 从路由参数获取信息
      const { group, title, instruction, levelId, nextLevel } = this.$route.query
      
      // 设置目标分组
      this.targetGroup = group || 'group1'
      
      this.title = title || '词汇'
      this.instruction = instruction || '输入词汇，按空格键可以听发音'
      this.levelId = levelId || 'keywords1'
      this.nextLevel = nextLevel || '/code-classroom/typing'
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.round(seconds % 60)
      return `${minutes}分${remainingSeconds}秒`
    },

    closeReport() {
      this.showComplete = false
      this.$router.back()
    },

    toggleMode() {
      this.isReverseMode = !this.isReverseMode;
      this.typedText = '';
      this.showHint = false;
      // 保存当前模式到localStorage
      //localStorage.setItem('typingPracticeMode', this.isReverseMode ? 'reverse' : 'forward');
      this.generateNewLetter();
    },

    toggleHint() {
      this.showHint = !this.showHint
    },

    handleReverseCorrectAnswer() {
      this.isCorrect = true;
      this.correctAttempts++;
      
      this.showScoreChange(50); // 完整单词给予更多分数
      this.score += 50;
      
      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/correct.mp3');
      audio.play();
      
      const progressIncrement = 100 / this.combinations;
      this.progress += progressIncrement;
      
      if (this.progress >= 100) {
        this.completeLevelAndSave();
      } else {
        setTimeout(() => {
          this.generateNewLetter();
        }, 800);
      }
      
      setTimeout(() => {
        this.isCorrect = false;
      }, 800);
    },
    
    handleReverseWrongAnswer() {
      this.isWrong = true;
      
      this.showScoreChange(-15); // 错误单词扣除更多分数
      this.score = Math.max(0, this.score - 15);
      
      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/fail.mp3');
      audio.play();
      
      setTimeout(() => {
        this.isWrong = false;
        this.typedText = ''; // 清空输入以便重新输入
      }, 800);
    },
    // 添加判断是否需要Shift键的方法
    isShiftRequired(char) {
      return /[A-Z!@#$%^&*()_+{}|:<>?]/.test(char);
    },
  },
  mounted() {
    this.loadRouteParams();
    
    // 从localStorage读取之前保存的练习模式
    //const savedMode = localStorage.getItem('typingPracticeMode');
    //if (savedMode === 'reverse') {
    //  this.isReverseMode = true;
    //}
    
    this.generateNewLetter();
    this.$el.focus();
    this.startTime = new Date();
    
    // 将焦点设置到游戏容器
    setTimeout(() => {
      this.$el.focus();
    }, 100);
  },
  // 添加路由参数变化时的处理
  watch: {
    '$route'() {
      this.loadRouteParams()
      this.restartLevel()
    }
  }
}
</script>

<style scoped>
.game-container {
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  outline: none;
  background: #F8FBFF;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
}

.game-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 20px 25px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(32, 107, 196, 0.08);
  margin-bottom: 30px;
  transition: all 0.3s ease;
}

.header-content {
  margin-bottom: 15px;
}

.game-header h1 {
  font-size: 30px;
  color: #1976D2;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
}

.instruction {
  font-size: 16px;
  color: #546E7A;
  margin: 10px 0;
  letter-spacing: 0.5px;
}

/* 进度条样式优化 */
.progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 15px;
}

.level-info {
  font-size: 14px;
  color: #546E7A;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 500;
}

.progress {
  height: 10px;
  background: #E3F2FD;
  border-radius: 6px;
  overflow: hidden;
  flex-grow: 1;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 6px;
  box-shadow: 0 1px 5px rgba(76, 175, 80, 0.3);
}

/* 统计区域样式优化 */
.stats-container {
  display: flex;
  justify-content: center;
  gap: 25px;
  margin-bottom: 30px;
}

.stat-box {
  background: white;
  padding: 18px 22px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(25, 118, 210, 0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 220px;
  position: relative;
  overflow: hidden;
}

.stat-box:hover {
  transform: translateY(-5px);
  border-color: rgba(25, 118, 210, 0.2);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.08);
  background-color: #FAFCFF;
}

.stat-box::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #1976D2, #64B5F6);
  opacity: 0;
  transition: opacity 0.4s;
}

.stat-box:hover::after {
  opacity: 1;
}

/* 图标样式优化 */
.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(100, 181, 246, 0.2));
  font-size: 24px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(25, 118, 210, 0.1);
}

.stat-box:hover .icon-circle {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 6px 15px rgba(25, 118, 210, 0.15);
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 26px;
  font-weight: bold;
  color: #1976D2;
  transition: all 0.3s ease;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.stat-box:hover .stat-value {
  color: #1565C0;
  transform: scale(1.05);
}

.stat-label {
  font-size: 14px;
  color: #546E7A;
  margin-top: 4px;
  font-weight: 500;
}

/* 得分变化动画优化 */
.score-change {
  position: absolute;
  top: 5px;
  right: 10px;
  font-size: 18px;
  font-weight: bold;
  animation: fadeUp 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  opacity: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.score-change.positive {
  color: #4CAF50;
}

.score-change.negative {
  color: #F44336;
}

@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  20% {
    opacity: 1;
    transform: translateY(-5px);
  }
  80% {
    opacity: 1;
    transform: translateY(-15px);
  }
  100% {
    opacity: 0;
    transform: translateY(-25px);
  }
}

/* 正确率条样式优化 */
.stat-value-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.accuracy-bar {
  height: 6px;
  background: #E3F2FD;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.accuracy-fill {
  height: 100%;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.5s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.accuracy-fill.high {
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
}

.accuracy-fill.medium {
  background: linear-gradient(90deg, #FF9800, #FFC107);
}

.accuracy-fill.low {
  background: linear-gradient(90deg, #F44336, #FF5722);
}

/* 打字区域样式优化 */
.typing-area {
  background: white;
  padding: 35px;
  border-radius: 16px;
  margin: 20px 0 30px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(25, 118, 210, 0.1);
  position: relative;
  overflow: hidden;
}

.typing-area::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(200, 230, 255, 0.2), transparent 70%);
  pointer-events: none;
}

.typing-area.correct {
  background: linear-gradient(to bottom, #e8f5e9, white);
  border-color: #A5D6A7;
  box-shadow: 0 10px 30px rgba(76, 175, 80, 0.15);
}

.typing-area.wrong {
  background: linear-gradient(to bottom, #ffebee, white);
  border-color: #FFCDD2;
  box-shadow: 0 10px 30px rgba(244, 67, 54, 0.15);
}

.keyword-info {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.keyword-name {
  font-size: 36px;
  font-weight: bold;
  color: #1976D2;
  margin-bottom: 8px;
  text-shadow: 0 2px 5px rgba(25, 118, 210, 0.2);
  letter-spacing: 1px;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.keyword-desc {
  font-size: 18px;
  color: #546E7A;
  margin-bottom: 15px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.audio-hint {
  font-size: 14px;
  background-color: #EEF2F6;
  padding: 6px 12px;
  border-radius: 50px;
  color: #546E7A;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.audio-hint:hover {
  background-color: #E3F2FD;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.audio-hint::before {
  content: "🔊";
  margin-right: 8px;
  font-size: 16px;
}

.target-letters {
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 25px;
  letter-spacing: 12px;
  line-height: 1.6;
}

.target-letters span {
  display: inline-block;
  padding: 0 10px;
  color: #78909C;
  position: relative;
  transition: all 0.3s ease;
}

.target-letters span.current {
  color: #1976D2;
  text-shadow: 0 1px 5px rgba(25, 118, 210, 0.3);
  border-bottom: 3px solid #1976D2;
  animation: pulse 1.2s infinite;
  transform: scale(1.1);
}

.target-letters span.completed {
  color: #4CAF50;
  text-shadow: 0 1px 3px rgba(76, 175, 80, 0.2);
}

.letter-instruction {
  font-size: 24px;
  color: #546E7A;
  margin: 20px 0;
  font-weight: 500;
}

.letter-highlight {
  position: relative;
  display: inline-block;
  font-size: 32px;
  color: #1976D2;
  font-weight: bold;
  text-shadow: 0 2px 5px rgba(25, 118, 210, 0.2);
  transition: all 0.3s ease;
  animation: highlight-pulse 2s infinite;
}

@keyframes highlight-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.letter-highlight.uppercase {
  color: #FF5722;
  text-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
}

.letter-highlight.special-char {
  color: #FF5722;
  text-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
}

.uppercase-hint {
  position: absolute;
  top: -18px;
  right: -18px;
  background: linear-gradient(135deg, #FF5722, #FF9800);
  color: white;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: bold;
  transform: rotate(10deg);
  box-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
  animation: float 3s infinite ease-in-out;
}

@keyframes float {
  0% { transform: rotate(10deg) translateY(0); }
  50% { transform: rotate(10deg) translateY(-5px); }
  100% { transform: rotate(10deg) translateY(0); }
}

/* 键盘显示区域优化 */
.practice-keyboard {
  background: linear-gradient(to bottom, #37474F, #263238);
  padding: 25px;
  border-radius: 16px;
  margin: 20px auto 30px;
  width: fit-content;
  transform: perspective(1000px) rotateX(5deg);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2), 0 5px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid #1e2b38;
  transition: all 0.3s ease;
}

.practice-keyboard:hover {
  transform: perspective(1000px) rotateX(2deg);
}

.practice-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  position: relative;
}

/* 错开排列，模拟真实键盘 */
.practice-row:nth-child(2) {
  padding-left: 12px;
}

.practice-row:nth-child(3) {
  padding-left: 18px;
}

.practice-row:nth-child(4) {
  padding-left: 25px;
}

.practice-key {
  width: 44px;
  height: 44px;
  background: linear-gradient(to bottom, #455A64, #37474F);
  color: white;
  border: none;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 15px;
  box-shadow: 0 4px 0 #1e2b38, 0 6px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  padding: 4px 2px;
}

.practice-key::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, rgba(255,255,255,0.12), rgba(255,255,255,0));
  border-radius: 7px 7px 0 0;
}

.practice-key.active {
  background: linear-gradient(to bottom, #1976D2, #1565C0);
  transform: translateY(4px);
  box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(0, 0, 0, 0.2);
}

.practice-key.active-shift {
  background: linear-gradient(to bottom, #FF5722, #E64A19);
  transform: translateY(4px);
  box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(0, 0, 0, 0.2);
  animation: pulsate 1s infinite;
}

@keyframes pulsate {
  0% { opacity: 1; box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(255, 87, 34, 0.4); }
  50% { opacity: 0.8; box-shadow: 0 0 10px rgba(255, 87, 34, 0.6), 0 2px 5px rgba(255, 87, 34, 0.4); }
  100% { opacity: 1; box-shadow: 0 0 0 #1e2b38, 0 2px 5px rgba(255, 87, 34, 0.4); }
}

/* 特殊键位的宽度优化 */
.backspace-key {
  width: 75px;
}

.tab-key {
  width: 65px;
}

.backslash-key {
  width: 55px;
}

.caps-key {
  width: 80px;
}

.enter-key {
  width: 85px;
}

.shift-key {
  width: 100px;
}

.shift-key-right {
  width: 100px;
}

.ctrl-key, .alt-key, .win-key, .menu-key {
  width: 55px;
}

.space-key {
  width: 280px;
  font-size: 15px;
  background: linear-gradient(to bottom, #546E7A, #455A64);
}

.space-key.active {
  background: linear-gradient(to bottom, #1976D2, #1565C0);
}

/* 控制按钮优化 */
.control-buttons {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.exit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 25px;
  background: linear-gradient(to bottom, #f44336, #d32f2f);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
  font-size: 16px;
  box-shadow: 0 4px 10px rgba(244, 67, 54, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.exit-button:hover {
  background: linear-gradient(to bottom, #e53935, #c62828);
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(244, 67, 54, 0.4);
}

.exit-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
}

.button-icon {
  margin-right: 10px;
  font-size: 1.2em;
}

/* 练习报告弹窗优化 */
.report-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.report-content {
  background: #ffffff;
  padding: 0;
  border-radius: 24px;
  max-width: 600px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: bounceIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  position: relative;
}

@keyframes bounceIn {
  0% { transform: scale(0.8); opacity: 0; }
  70% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

.report-content h2 {
  font-size: 28px;
  margin: 0;
  padding: 25px 0;
  color: #ffffff;
  background: linear-gradient(135deg, #4A6FFF, #9C4AFF);
  border-radius: 24px 24px 0 0;
  position: relative;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.report-content h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px;
  position: relative;
}

.report-stats::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="10" cy="10" r="2" fill="%23f0f0f0"/><circle cx="30" cy="10" r="2" fill="%23f0f0f0"/><circle cx="50" cy="10" r="2" fill="%23f0f0f0"/><circle cx="70" cy="10" r="2" fill="%23f0f0f0"/><circle cx="90" cy="10" r="2" fill="%23f0f0f0"/><circle cx="10" cy="30" r="2" fill="%23f0f0f0"/><circle cx="30" cy="30" r="2" fill="%23f0f0f0"/><circle cx="50" cy="30" r="2" fill="%23f0f0f0"/><circle cx="70" cy="30" r="2" fill="%23f0f0f0"/><circle cx="90" cy="30" r="2" fill="%23f0f0f0"/><circle cx="10" cy="50" r="2" fill="%23f0f0f0"/><circle cx="30" cy="50" r="2" fill="%23f0f0f0"/><circle cx="50" cy="50" r="2" fill="%23f0f0f0"/><circle cx="70" cy="50" r="2" fill="%23f0f0f0"/><circle cx="90" cy="50" r="2" fill="%23f0f0f0"/><circle cx="10" cy="70" r="2" fill="%23f0f0f0"/><circle cx="30" cy="70" r="2" fill="%23f0f0f0"/><circle cx="50" cy="70" r="2" fill="%23f0f0f0"/><circle cx="70" cy="70" r="2" fill="%23f0f0f0"/><circle cx="90" cy="70" r="2" fill="%23f0f0f0"/><circle cx="10" cy="90" r="2" fill="%23f0f0f0"/><circle cx="30" cy="90" r="2" fill="%23f0f0f0"/><circle cx="50" cy="90" r="2" fill="%23f0f0f0"/><circle cx="70" cy="90" r="2" fill="%23f0f0f0"/><circle cx="90" cy="90" r="2" fill="%23f0f0f0"/></svg>');
  z-index: -1;
  opacity: 0.3;
}

.stat-item {
  padding: 20px 15px;
  background: #f8f9ff;
  border-radius: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  border: 2px solid #f0f0f0;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

@keyframes fadeInUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.stat-item:nth-child(1) { animation-delay: 0.1s; }
.stat-item:nth-child(2) { animation-delay: 0.2s; }
.stat-item:nth-child(3) { animation-delay: 0.3s; }
.stat-item:nth-child(4) { animation-delay: 0.4s; }

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: #e0e7ff;
  background: #f0f7ff;
}

.stat-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4A6FFF, #9C4AFF);
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 3px 3px 0 0;
}

.stat-item:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
  font-weight: 600;
  text-align: center;
}

.stat-label::before {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #4A6FFF;
  text-shadow: 0 2px 4px rgba(74, 111, 255, 0.2);
  position: relative;
  display: inline-block;
  text-align: center;
  margin-top: 5px;
}

.keyword-summary {
  margin: 0 30px 30px;
  padding: 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
}

.keyword-summary h3 {
  color: #4A6FFF;
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 700;
}

.keyword-summary p {
  margin: 0;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

.keyword-summary .highlight {
  background: linear-gradient(135deg, #4A6FFF, #9C4AFF);
  background-clip: text;
    -webkit-background-clip: text;
    background-clip: text;
  color: transparent;
  font-weight: 700;
  padding: 0 5px;
  position: relative;
}

.keyword-summary .highlight::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4A6FFF, #9C4AFF);
  opacity: 0.2;
  border-radius: 3px;
}

.report-message {
  margin: 0 30px 30px;
  padding: 25px 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
  text-align: center;
}

.report-message::before {
  content: '';
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background: #4A6FFF;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message::after {
  content: '';
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 100px;
  height: 100px;
  background: #9C4AFF;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message .result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.report-message img {
  width: 120px;
  height: 120px;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  animation: pulse 2s infinite ease-in-out;
  transform-origin: center;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.report-message p {
  font-size: 20px;
  color: #4A6FFF;
  font-weight: 600;
  margin: 10px 0 0;
  padding: 0;
  position: relative;
  display: block;
  text-align: center;
}

.report-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  padding: 0 30px 30px;
  animation: fadeIn 0.6s ease-out 0.7s forwards;
  opacity: 0;
}

.report-buttons button {
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  min-width: 140px;
}

.report-buttons button:first-child {
  background: linear-gradient(135deg, #4A6FFF, #9C4AFF);
  color: white;
}

.report-buttons button:first-child:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(74, 111, 255, 0.3);
}

.report-buttons button:first-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(74, 111, 255, 0.2);
}

.report-buttons button:last-child {
  background: white;
  color: #666;
  border: 2px solid #eee;
}

.report-buttons button:last-child:hover {
  background: #f8f9ff;
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.05);
  border-color: #e0e7ff;
  color: #4A6FFF;
}

.report-buttons button:last-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.03);
}

.report-buttons button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
  transform: translateY(-100%);
  transition: transform 0.6s;
}

.report-buttons button:hover::before {
  transform: translateY(0);
}

/* 响应式布局优化 */
@media (max-width: 768px) {
  .game-container {
    padding: 15px;
  }
  
  .game-header {
    padding: 15px 20px;
  }
  
  .game-header h1 {
    font-size: 24px;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
  
  .stat-box {
    max-width: 100%;
    width: 100%;
  }
  
  .typing-area {
    padding: 25px 15px;
  }
  
  .keyword-name {
    font-size: 28px;
  }
  
  .keyword-desc {
    font-size: 16px;
  }
  
  .target-letters {
    font-size: 36px;
    letter-spacing: 8px;
  }
  
  .letter-instruction {
    font-size: 20px;
  }
  
  .letter-highlight {
    font-size: 26px;
  }
  
  /* 移动设备键盘布局优化 */
  .practice-keyboard {
    padding: 15px 10px;
    overflow-x: auto;
    max-width: 100%;
    transform: perspective(1000px) rotateX(3deg);
  }
  
  .practice-row {
    margin-bottom: 4px;
    flex-wrap: nowrap;
    min-width: 540px; /* 确保键盘不会被压缩太小 */
    gap: 4px;
  }
  
  .practice-key {
    width: 35px;
    height: 35px;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  /* 移动设备上特殊键的宽度调整 */
  .backspace-key {
    width: 60px;
  }
  
  .tab-key {
    width: 50px;
  }
  
  .backslash-key {
    width: 45px;
  }
  
  .caps-key {
    width: 65px;
  }
  
  .enter-key {
    width: 70px;
  }
  
  .shift-key, .shift-key-right {
    width: 75px;
  }
  
  .ctrl-key, .alt-key, .win-key, .menu-key {
    width: 45px;
  }
  
  .space-key {
    width: 180px;
  }
  
  .report-content {
    padding: 25px 20px;
  }
  
  .report-content h2 {
    font-size: 22px;
  }
  
  .report-stats {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .report-message img {
    width: 80px;
    height: 80px;
  }
  
  .report-message p {
    font-size: 16px;
  }
  
  .report-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .report-buttons button {
    width: 100%;
  }
}

/* 添加模式切换按钮 */
.mode-switch-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.mode-switch-button {
  padding: 12px 25px;
  background: linear-gradient(to bottom, #1976D2, #1565C0);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 16px;
  box-shadow: 0 6px 15px rgba(25, 118, 210, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.mode-switch-button.active {
  background: linear-gradient(to bottom, #1565C0, #0D47A1);
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(25, 118, 210, 0.4);
}

.mode-description {
  font-size: 16px;
  color: #546E7A;
  font-weight: 500;
}

/* 反向模式样式 */
.keyword-question {
  font-size: 22px;
  color: #546E7A;
  margin-bottom: 15px;
  font-weight: 600;
}

.keyword-name.reverse {
  font-size: 32px;
  font-weight: bold;
  color: #1976D2;
  margin-bottom: 12px;
  text-shadow: 0 2px 5px rgba(25, 118, 210, 0.2);
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #1976D2, #64B5F6);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  padding: 5px 15px;
  border-radius: 8px;
  border: 2px dashed rgba(25, 118, 210, 0.2);
  display: inline-block;
}

.keyword-hint {
  background-color: #FFF8E1;
  padding: 8px 15px;
  border-radius: 8px;
  margin-top: 10px;
  margin-bottom: 15px;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease;
  box-shadow: 0 3px 10px rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.hint-label {
  font-weight: 600;
  color: #F57C00;
  margin-right: 5px;
}

.hint-text {
  color: #FF9800;
  font-weight: 500;
}

.user-input {
  margin: 25px 0;
  text-align: center;
}

.input-prompt {
  display: block;
  font-size: 18px;
  color: #546E7A;
  margin-bottom: 15px;
  font-weight: 500;
}

.input-display {
  display: inline-flex;
  padding: 12px 25px;
  background: rgba(25, 118, 210, 0.05);
  border: 2px solid rgba(25, 118, 210, 0.1);
  border-radius: 8px;
  font-size: 24px;
  color: #1976D2;
  font-weight: 500;
  min-width: 200px;
  min-height: 35px;
  position: relative;
  justify-content: center;
  transition: all 0.3s ease;
}

.input-display:focus {
  border-color: rgba(25, 118, 210, 0.3);
  background: rgba(25, 118, 210, 0.08);
  box-shadow: 0 3px 10px rgba(25, 118, 210, 0.1);
}

.typed-text {
  letter-spacing: 1px;
}

.cursor-indicator {
  display: inline-block;
  width: 2px;
  height: 25px;
  background-color: #1976D2;
  margin-left: 2px;
  animation: blink 0.8s infinite;
  align-self: center;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.hint-container {
  margin-top: 20px;
}

.hint-button {
  padding: 10px 20px;
  background: linear-gradient(to bottom, #FF9800, #F57C00);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 10px rgba(255, 152, 0, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.hint-button:hover {
  background: linear-gradient(to bottom, #F57C00, #EF6C00);
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(255, 152, 0, 0.4);
}

.hint-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 添加双键位按键样式 */
.key-top, .key-bottom, .key-single {
  display: block;
  line-height: 1.2;
  width: 100%;
  text-align: center;
}

.key-top {
  font-size: 11px;
  color: #FFD54F;
  margin-bottom: 2px;
  position: relative;
  top: -2px;
}

.key-bottom {
  font-size: 15px;
  position: relative;
  top: 1px;
}

.key-single {
  font-size: 16px;
}

/* 响应式布局优化 */
@media (max-width: 768px) {
  .game-container {
    padding: 15px;
  }
  
  .game-header {
    padding: 15px 20px;
  }
  
  .game-header h1 {
    font-size: 24px;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
  
  .stat-box {
    max-width: 100%;
    width: 100%;
  }
  
  .typing-area {
    padding: 25px 15px;
  }
  
  .keyword-name {
    font-size: 28px;
  }
  
  .keyword-desc {
    font-size: 16px;
  }
  
  .target-letters {
    font-size: 36px;
    letter-spacing: 8px;
  }
  
  .letter-instruction {
    font-size: 20px;
  }
  
  .letter-highlight {
    font-size: 26px;
  }
  
  /* 移动设备键盘布局优化 */
  .practice-keyboard {
    padding: 15px 10px;
    overflow-x: auto;
    max-width: 100%;
    transform: perspective(1000px) rotateX(3deg);
  }
  
  .practice-row {
    margin-bottom: 4px;
    flex-wrap: nowrap;
    min-width: 540px; /* 确保键盘不会被压缩太小 */
    gap: 4px;
  }
  
  .practice-key {
    width: 35px;
    height: 35px;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  /* 移动设备上特殊键的宽度调整 */
  .backspace-key {
    width: 60px;
  }
  
  .tab-key {
    width: 50px;
  }
  
  .backslash-key {
    width: 45px;
  }
  
  .caps-key {
    width: 65px;
  }
  
  .enter-key {
    width: 70px;
  }
  
  .shift-key, .shift-key-right {
    width: 75px;
  }
  
  .ctrl-key, .alt-key, .win-key, .menu-key {
    width: 45px;
  }
  
  .space-key {
    width: 180px;
  }
  
  .report-content {
    padding: 25px 20px;
  }
  
  .report-content h2 {
    font-size: 22px;
  }
  
  .report-stats {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .report-message img {
    width: 80px;
    height: 80px;
  }
  
  .report-message p {
    font-size: 16px;
  }
  
  .report-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .report-buttons button {
    width: 100%;
  }
  
  .key-top {
    font-size: 10px;
  }
  
  .key-bottom {
    font-size: 14px;
  }
  
  .key-single {
    font-size: 15px;
  }
}

.special-char-hint {
  position: absolute;
  top: -18px;
  right: -18px;
  background: linear-gradient(135deg, #FF5722, #FF9800);
  color: white;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: bold;
  transform: rotate(10deg);
  box-shadow: 0 2px 5px rgba(255, 87, 34, 0.3);
  animation: float 3s infinite ease-in-out;
}

@keyframes float {
  0% { transform: rotate(10deg) translateY(0); }
  50% { transform: rotate(10deg) translateY(-5px); }
  100% { transform: rotate(10deg) translateY(0); }
}
</style>